import { Connector } from 'wagmi';
import { EthereumProvider } from '@walletconnect/ethereum-provider';
import { EthereumWallet } from '@blink/components/src/utils/wallet/ethereumWallet';
import Logger from '@blink/components/src/utils/logger';
import { WalletAddress } from '@blink/components/src/types';
import { NETWORK, NETWORK_CHAIN } from '@blink/components/src/constants/global';
import { SendTransactionProps } from '@blink/components/src/utils';
import { RequestArguments } from '@walletconnect/ethereum-provider/dist/types/types';
import { markRpcRateLimited } from '@blink/components/src/utils/rpcPool';
import { Api } from '@blink/components';

import { getWagmiWalletState } from '@/shared/utils/getWagmiWalletState';

type Request = <T = unknown>(args: RequestArguments, expiry?: number) => Promise<T>;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
type Provider = EthereumProvider;

const retryWithBackoffAndReconnect = async <T>(
    fn: () => Promise<T>,
    reconnect: () => Promise<void>,
    shouldRetry: (error: any) => boolean,
    maxRetries = 5,
    baseDelay = 1000,
): Promise<T> => {
    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            if (!shouldRetry(error) || attempt === maxRetries) {
                Logger.error('Max retries reached for request', { error });
                throw error;
            }
            Logger.error('Retry request failed:', { error, attempt: attempt + 1 });
            await reconnect();
            const delay = Math.pow(2, attempt) * baseDelay;
            await new Promise((res) => setTimeout(res, delay));
        }
    }

    throw lastError;
};

export class NewWalletConnect extends EthereumWallet {
    private provider: Provider;

    constructor() {
        super();
    }

    public getProjectMetaData() {
        return {
            name: 'Arkis',
            description: 'Arkis Web3Wallet for WalletConnect',
            url: Api.WC_HOME_URL as string,
            icons: [`${Api.WC_HOME_URL}/logo192.svg` as string],
        };
    }

    public isWalletInProvider(): boolean {
        return !!getWagmiWalletState().connector;
    }

    public isAvailable(): boolean {
        return this.isWalletInProvider();
    }

    public async getProvider(): Promise<any> {
        const connector: Connector = getWagmiWalletState()?.connector;

        if (!connector) {
            return undefined;
        }

        if (this.provider) {
            return this.provider;
        }

        try {
            this.provider = (await connector.getProvider()) as Provider;

            this.overrideRequestMethod();

            return this.provider;
        } catch (error) {
            Logger.error('Error getting provider from connector', {
                error,
            });
            return undefined;
        }
    }

    private overrideRequestMethod(): void {
        if (!this.provider) return;

        const originalRequest = this.provider.request.bind(this.provider) as Request;

        this.provider.request = (async (args: RequestArguments, expiry?: number) => {
            return retryWithBackoffAndReconnect(
                () => originalRequest(args, expiry),
                async () => {
                    Logger.warn('Reconnecting WalletConnect provider due to error');

                    const currentRpc =
                        this.provider?.rpc?.rpcMap?.[Number(NETWORK_CHAIN.ETHEREUM_NETWORK)];

                    markRpcRateLimited(currentRpc);
                },
                (error) => error?.code === -32005 || error?.message === 'rate limit exceeded',
            );
        }) as Request;
    }

    public async changeNetwork(network: NETWORK): Promise<void> {
        await super.changeNetwork(network);
    }

    public async connect(): Promise<WalletAddress[]> {
        const isConnected = await this.isConnected();
        const provider = await this.getProvider();

        if (isConnected && provider.accounts && provider.accounts?.length) {
            return provider.accounts;
        }

        await provider.connect();

        return provider.accounts;
    }

    public async disconnect(): Promise<void> {
        const provider = await this.getProvider();
        await provider?.disconnect();
    }

    public async getChainId(): Promise<string> {
        const provider = await this.getProvider();
        return provider.chainId;
    }

    public async getNetwork(): Promise<NETWORK_CHAIN> {
        const provider = await this.getProvider();
        return provider.chainId;
    }

    public async isConnected(): Promise<boolean> {
        const provider = await this.getProvider();
        return provider.connected;
    }

    public async sendTransaction(data: SendTransactionProps): Promise<void> {
        const provider = await this.getProvider();
        await provider.request(data);
    }
}
